import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Switch, Route, useLocation, useHistory, Link } from 'react-router-dom'
import { useGlobalState } from '@/state'

import Banners from '@/Layout/Banners'
import ErrorHandler from '@/Layout/ErrorHander'
import ErrorNotification from '@/shared/ErrorNotification'
import { Toast } from '@/shared/Toast'
import Loading from '@/shared/Loading'

import Breadcrumbs from '@/Layout/Breadcrumbs'
import PageTitle from '@/Layout/PageTitle'
import ChatsIndex from '@/pages/ChatsIndex'
import CourseChat from '@/pages/content/CourseChat'
import ChatsContainer from '@/containers/ChatsContainer'
import SelectCourseContainer from '@/containers/SelectCourseContainer'
import ConceptsContainer from '@/containers/ConceptsContainer'
import ContentContainer from '@/containers/ContentContainer'
import DashboardContainer from '@/containers/DashboardContainer'
import EventsContainer from '@/containers/EventsContainer'
import WorkoutsContainer from '@/containers/WorkoutsContainer'
import OnboardingContainer from '@/containers/OnboardingContainer'
import PromoCodeContainer from '@/containers/PromoCodeContainer'
import SettingsContainer from '@/containers/SettingsContainer'
import BillingHistory from '@/pages/Billing/BillingHistory'
import Receipt from '@/pages/Billing/Receipt'
import ProfileEdit from '@/pages/ProfileEdit'
import ProfilePublic from '@/pages/ProfilePublic'
import SubscriptionContainer from '@/containers/SubscriptionContainer'
import ChangelogIndex from '@/pages/Changelog/ChangelogIndex'
import JobShow from '@/pages/JobShow'
import Notifications from '@/pages/Notifications'
import ChangelogAnnouncementModal from '@/shared/ChangelogAnnouncementModal'
import FeedbackContainer from '@/containers/FeedbackContainer'

const NotFound = () => (
  <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg mt-8">
    <div className="px-4 py-5 sm:p-6">
      <div className="sm:flex sm:items-start sm:justify-between">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Not Found</h3>
          <div className="mt-2 max-w-xl text-sm text-gray-500 dark:text-gray-200">
            <p> I think that link was broken. Please report this to support@closingcredits.com if you don't mind and we'll get it fixed  </p>
          </div>
        </div>
      </div>
    </div>
  </div>
)


const NO_SIDEBAR = ['/onboarding', '/select_course', '/subscriptions', '/select_enrollment_type', '/promo_code']
const NO_HEADER = ['/onboarding', '/select_course', '/select_enrollment_type']

export default function App(props) {
  // eslint-disable-next-line no-unused-vars
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  let history = useHistory()
  const isComponentMounted = useRef(false)
  const location = useLocation()
  const activePath = location.pathname
  // Routes that fill the content area instead of sitting in the centred
  // max-width container.
  // /chats itself is the room list and stays in the container; /chats/:slug
  // is a room and fills the space, same as the legacy /c/:id/chat.
  const isFullBleedRoute = /^\/c\/[^/]+\/chat\/?$/.test(activePath) ||
                           /^\/chats\/[^/]+\/?$/.test(activePath)

  useEffect(() => {
    if (activePath === '/onboarding') { return }

    // redirect in reverse order
    const leadPaths = ['/subscriptions', '/select_enrollment_type', '/change_card', '/promo_code']
    if (currentUser.needsOnboarding) {
      history.push('/onboarding')
    }
  }, [activePath, currentUser])

  useEffect(() => {
    setCurrentUser(props.currentUser)

    isComponentMounted.current = true
  }, [])

  if (!isComponentMounted.current) {
    return <Loading full={true} />
  }

  // No shell here. application_spa.html.erb owns the sidebar, the scroll
  // container and the full-height box; this used to render a second
  // h-screen/main/container inside that one, which gave the app two nested
  // 100vh boxes and two nested scrollers.
  return (
    <div className="flex flex-col min-h-full md:h-full">
      <Banners />
      {/* The chat is a fill-the-space app view rather than a document, so it
          opts out of the centred, max-width container every other route uses.
          `container mx-auto` would otherwise leave dead margins on wide
          screens and cap the message feed mid-page. */}
      <div className={isFullBleedRoute
        ? 'flex-1 min-h-0 flex flex-col w-full px-3 sm:px-4 lg:px-6'
        : 'container mx-auto px-4 sm:px-6 lg:px-8 pb-10'}
      >
            <ErrorHandler>
              <Switch>
                <Route {...props} exact path='/dashboard'>
                  <DashboardContainer />
                </Route>
                <Route {...props} exact path='/onboarding'>
                  <OnboardingContainer />
                </Route>
                <Route {...props} exact path={['/select_course','/reserve_seat', '/select_enrollment_type', '/finalize_enrollment']}>
                  <SelectCourseContainer />
                </Route>
                <Route {...props} exact path='/settings/billing/receipts/:receiptNumber'>
                  <Breadcrumbs>
                    <Link to='/settings' className='text-sm font-medium text-slate-500 hover:text-clcpurple dark:text-slate-300'>Settings</Link>
                    <Link to='/settings/billing' className='text-sm font-medium text-slate-500 hover:text-clcpurple dark:text-slate-300'>Billing</Link>
                  </Breadcrumbs>
                  <PageTitle pagetitle='Receipt' />
                  <Receipt />
                </Route>
                <Route {...props} exact path='/settings/billing'>
                  <Breadcrumbs>
                    <Link to='/settings' className='text-sm font-medium text-slate-500 hover:text-clcpurple dark:text-slate-300'>Settings</Link>
                  </Breadcrumbs>
                  <PageTitle pagetitle='Billing history' />
                  <BillingHistory />
                </Route>
                <Route {...props} exact path='/settings'>
                  <SettingsContainer />
                </Route>
                <Route {...props} exact path='/profile'>
                  <ProfileEdit />
                </Route>
                <Route {...props} exact path='/profiles/:username'>
                  <ProfilePublic />
                </Route>
                <Route {...props} exact path={['/events', '/events/:id', '/past-events', '/myevents']}>
                  <EventsContainer />
                </Route>
                <Route {...props} exact path={['/workouts', '/workouts/:id', '/myworkouts']}>
                  <WorkoutsContainer />
                </Route>
                <Route {...props} exact path='/concepts'>
                  <ConceptsContainer />
                </Route>
                <Route {...props} exact path='/chat'>
                  <ChatsContainer />
                </Route>
                {/* /chat is the Discord page; /chats is the course-chat inbox
                    the sidebar's chat icon points at. */}
                <Route {...props} exact path='/chats'>
                  <Breadcrumbs>
                    <span className='text-sm font-medium text-slate-700 dark:text-slate-200'>Chats</span>
                  </Breadcrumbs>
                  <PageTitle pagetitle='Chats' />
                  <ChatsIndex />
                </Route>
                {/* Cohort and community rooms. The legacy course-wide room
                    keeps its own /c/:id/chat route inside ContentContainer. */}
                <Route {...props} exact path='/chats/:slug'>
                  <CourseChat />
                </Route>
                <Route {...props} path={['/subscriptions', '/buy_credits', '/change_card']}>
                  <SubscriptionContainer />
                </Route>
                <Route {...props} path={['/c/:courseId', '/l/:lessonId', '/library/:contentId', '/course-material/:courseId']}>
                  <ContentContainer />
                </Route>
                <Route {...props} exact path='/promo_code'>
                  <PromoCodeContainer />
                </Route>
                <Route {...props} exact path='/changelog'>
                  <ChangelogIndex />
                </Route>
                <Route {...props} exact path='/notifications'>
                  <Notifications />
                </Route>
                <Route {...props} exact path='/jobs/:id'>
                  <JobShow />
                </Route>
                <Route {...props} path={['/feedback', '/feedback/new', '/feedback/:slug']}>
                  <FeedbackContainer />
                </Route>
                <Route exact path='/' component={() => {
                  // not a SPA path, used for redirects only until converted to SPA
                  window.location.href = '/'
                  return null
                }}/>
                <Route component={NotFound} />
              </Switch>
            </ErrorHandler>
          </div>
      <ErrorNotification />
      <Toast />
      <ChangelogAnnouncementModal />
    </div>
  )
}

App.propTypes = {
  currentUser: PropTypes.object.isRequired
}
